/* =========================================================================
   Shared mutable runtime context. The prototype used module-global `engine` /
   `game` plus a handful of shared UI helpers; this object is the typed
   equivalent, letting the game controller and the mini-game / overlay modules
   cooperate without import cycles at evaluation time (all access is at call
   time, inside functions/handlers — exactly as the prototype did).
   ========================================================================= */
import type { RoundEngine, LogEntry } from './engine';

export interface Achievement {
  icon: string;
  name: string;
  desc: string;
}

export interface GameState {
  bank: number;
  roundIndex: number;
  log: LogEntry[];
  achievements: Achievement[];
}

export interface AppContext {
  engine: RoundEngine | null;
  game: GameState;
  /** True while a mini-game runs in the post-game Arcade SANDBOX. Item grants
      (wheel item slots) and their telemetry are suppressed in this mode so the
      infinitely-replayable arcade can't farm real inventory / quest progress. */
  arcadeMode: boolean;
  /** Advance to the next question (or complete the round). Set by the controller. */
  nextQuestion(): void;
  /** Repaint the persistent bank pill from engine.bankCents. Set by the controller. */
  updateBank(): void;
  /** Fire the floating +/- delta animation near the bank pill. Set by the controller. */
  floatDelta(cents: number): void;
  /** Record a break-award achievement (deduped by name). Set by the controller. */
  addAchievement(icon: string, name: string, desc: string): void;
}

function freshGame(): GameState {
  return { bank: 0, roundIndex: 0, log: [], achievements: [] };
}

const noop = (): void => {};

export const ctx: AppContext = {
  engine: null,
  game: freshGame(),
  arcadeMode: false,
  nextQuestion: noop,
  updateBank: noop,
  floatDelta: noop,
  addAchievement: noop,
};

export function resetGame(): void {
  ctx.game = freshGame();
}
